import React, { Suspense, lazy, useEffect, useState } from "react";
import "@/App.css";
import "@/navi-ux.css";
import "@/navi-premium.css";
import "@/navi-final.css";   // last word on brand lockup + plan cards (single-line specs, island entrance)
import "@/navi-fluid.css";   // full-width layout on every screen size + premium phone layout (loads last)
import "@/navi-book.css";    // Book Now colour + fast premium service menu
import "@/navi-atm-pass.css";   // Navi Pass: ATM / credit-card size
import "@/navi-mobile-groups.css";   // phone: grouped/minimised cards, compact tables, customer lens dock (loads very last)
import "@/navi-dashboard-premium.css";   // customer dashboard: premium, organised, foldable sections (loads last)
import "@/navi-dashboard-purple.css";   // customer dashboard: white + purple theme (loads after everything else)
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider, useAuth } from "@/lib/auth";
import { ThemeProvider } from "@/lib/theme";
import "@/lib/installApp"; // capture the browser install prompt as early as possible


/*
 * lazy() with recovery. After a new deployment an installed app may still hold the old page, whose screen files
 * no longer exist: the import fails and the screen stays blank. We retry once, then clear the app cache and reload
 * once (guarded so it can never loop).
 */
function lazyRetry(factory) {
  return lazy(() => factory().catch(async (err) => {
    try {
      if (!sessionStorage.getItem("navi_chunk_reload")) {
        sessionStorage.setItem("navi_chunk_reload", "1");
        if (window.caches) { const keys = await caches.keys(); await Promise.all(keys.map((k) => caches.delete(k))); }
        const regs = await navigator.serviceWorker?.getRegistrations?.();
        if (regs) await Promise.all(regs.map((r) => r.update().catch(() => {})));
        window.location.reload();
        return new Promise(() => {});          // keep the loader up while the page reloads
      }
    } catch { /* fall through */ }
    throw err;
  }));
}
window.addEventListener("load", () => setTimeout(() => { try { sessionStorage.removeItem("navi_chunk_reload"); } catch {} }, 8000));

// Landing + loader stay in the first bundle (fast first paint); every other page downloads only when it is opened.
import Landing from "@/pages/Landing";
import BrandLoader from "@/components/BrandLoader";
import PaymentRetryBar from "@/components/PaymentRetryBar";
import OfflineBanner from "@/components/OfflineBanner";
const AuthCallback = lazyRetry(() => import("@/pages/AuthCallback"));
const DashboardLayout = lazyRetry(() => import(/* webpackChunkName: "dashboard-layout" */ "@/components/DashboardLayout"));
const Dashboard = lazyRetry(() => import(/* webpackChunkName: "dashboard" */ "@/pages/Dashboard"));
const Bookings = lazyRetry(() => import(/* webpackChunkName: "bookings" */ "@/pages/Bookings"));
const Vehicles = lazyRetry(() => import("@/pages/Vehicles"));
const Subscription = lazyRetry(() => import(/* webpackChunkName: "subscription" */ "@/pages/Subscription"));
const Profile = lazyRetry(() => import("@/pages/Profile"));
const Admin = lazyRetry(() => import("@/pages/Admin"));
const ExtraServicesAdmin = lazyRetry(() => import("@/pages/ExtraServicesAdmin"));
const Addons = lazyRetry(() => import("@/pages/Addons"));
const PaymentSuccess = lazyRetry(() => import("@/pages/PaymentSuccess"));
const PaymentCancel = lazyRetry(() => import("@/pages/PaymentCancel"));
const Services = lazyRetry(() => import("@/pages/Services"));
const ReferralLanding = lazyRetry(() => import("@/pages/ReferralLanding"));
const Referral = lazyRetry(() => import("@/pages/Referral"));
const PartnerHub = lazyRetry(() => import("@/pages/PartnerHub"));
const Careers = lazyRetry(() => import("@/pages/Careers"));
const ApplyLink = lazyRetry(() => import("@/pages/ApplyLink"));
const ServiceBooking = lazyRetry(() => import("@/pages/ServiceBooking"));
const JobsAdmin = lazyRetry(() => import("@/pages/JobsAdmin"));
const Policy = lazyRetry(() => import("@/pages/Policy"));
const Policies = lazyRetry(() => import("@/pages/Policies"));
const SeoLanding = lazyRetry(() => import("@/pages/SeoLanding"));
const OurServices = lazyRetry(() => import("@/pages/OurServices"));
const LocationHyderabad = lazyRetry(() => import("@/pages/LocationHyderabad"));
const QrSubscribe = lazyRetry(() => import(/* webpackChunkName: "qr-subscribe" */ "@/pages/QrSubscribe"));
const NaviChatBot = lazyRetry(() => import("@/components/NaviChatBot"));


/*
 * Protected routes
 *
 * Wait for AuthProvider to finish checking the existing
 * login session before deciding whether the user is logged in.
 */
function Protected({ children, adminOnly = false, panel = "" }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <BrandLoader />
    );
  }

  /*
   * No authenticated user.
   */
  if (!user) {
    // Deliberate log out -> landing page. Anything else (opening /dashboard while
    // signed out) -> landing page with the sign-in popup.
    let loggedOut = false;
    try { loggedOut = sessionStorage.getItem("nw_logged_out") === "1"; sessionStorage.removeItem("nw_logged_out"); } catch {}
    return <Navigate to={loggedOut ? "/" : "/login"} replace />;
  }

  /*
   * Admin-only route.
   */
  const salesOnly = !user.is_admin && user.role !== "admin" && (
    ["sales_manager", "sales_executive"].includes(user.role) ||
    (["staff", "technician"].includes(user.role) && /sales|market/i.test(user.role_title || ""))
  );
  const adminEligible = user.is_admin || user.role === "admin" || ["staff", "technician"].includes(user.role);
  if (adminOnly && (!adminEligible || salesOnly)) {
    return <Navigate to="/dashboard" replace />;
  }

  /*
   * Separate admin roles: "carwash" admins only use /admin, "extra" admins only use /admin/extra-services.
   */
  const scope = user.is_admin || user.role === "admin" ? (user.admin_scope || "all") : "";
  const home = scope === "jobs" ? "/admin/jobs" : scope === "extra" ? "/admin/extra-services" : "/admin";
  if (panel === "carwash" && (scope === "extra" || scope === "jobs")) return <Navigate to={home} replace />;
  if (panel === "extra" && (scope === "carwash" || scope === "jobs")) return <Navigate to={home} replace />;
  if (panel === "jobs" && (scope === "carwash" || scope === "extra")) return <Navigate to={home} replace />;

  return children;
}


/*
 * Navi assistant - shown on every public and customer page (landing, login,
 * dashboard...). Hidden on the admin console. Re-mounted on login/logout so the
 * greeting and suggestions match the visitor's state.
 */
function GlobalChatBot() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const go = () => setReady(true);
    if (typeof window.requestIdleCallback === "function") { const id = window.requestIdleCallback(go, { timeout: 4000 }); return () => window.cancelIdleCallback?.(id); }
    const t = setTimeout(go, 2500); return () => clearTimeout(t);
  }, []);
  if (pathname.startsWith("/admin") || !ready) return null;
  return <Suspense fallback={null}><NaviChatBot key={user ? "member" : "guest"} /></Suspense>;
}


/*
 * Application routes
 */
function HomeEntry() {
  const { user, loading } = useAuth();
  const standalone = typeof window !== "undefined" && (window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true);
  if (standalone && loading) return <BrandLoader />;
  if (standalone && user) return <Navigate to="/dashboard" replace />;
  return <Landing />;
}

function AppRouter() {
  const location = useLocation();

  /*
   * Backward compatibility for the old OAuth callback.
   *
   * New Google authentication does NOT use session_id.
   */
  if (location.hash?.includes("session_id=")) {
    return <Suspense fallback={<BrandLoader />}><AuthCallback /></Suspense>;
  }

  return (
    <Suspense fallback={<BrandLoader />}>
    <Routes>

      {/* Public */}
      <Route
        path="/"
        element={<HomeEntry />}
      />

      {/* Login is a popup on the landing page; this path simply opens it. */}
      <Route
        path="/login"
        element={<Landing />}
      />

      <Route
        path="/payment/success"
        element={<PaymentSuccess />}
      />

      <Route
        path="/payment/cancel"
        element={<PaymentCancel />}
      />

      {/* Direct-to-subscription page for QR codes (not in the sitemap, noindex) */}
      <Route path="/qr-subscribe" element={<QrSubscribe />} />

      <Route path="/ref/:code" element={<ReferralLanding />} />
      <Route path="/partner" element={<PartnerHub />} />
      <Route path="/careers" element={<Careers />} />
      <Route path="/apply/:code" element={<ApplyLink />} />
      <Route path="/policies" element={<Policies />} />
      <Route path="/policy/:slug" element={<Policy />} />

      {/* SEO keyword pages (see lib/seoPages.js + public/sitemap.xml) */}
      <Route path="/doorstep-car-wash-hyderabad" element={<SeoLanding slug="doorstep-car-wash-hyderabad" />} />
      <Route path="/car-wash-subscription-hyderabad" element={<SeoLanding slug="car-wash-subscription-hyderabad" />} />
      <Route path="/apartment-car-wash-hyderabad" element={<SeoLanding slug="apartment-car-wash-hyderabad" />} />
      <Route path="/car-wash-hyderabad" element={<SeoLanding slug="car-wash-hyderabad" />} />
      <Route path="/car-interior-cleaning-hyderabad" element={<SeoLanding slug="car-interior-cleaning-hyderabad" />} />
      <Route path="/car-polishing-hyderabad" element={<SeoLanding slug="car-polishing-hyderabad" />} />
      <Route path="/ac-service-hyderabad" element={<SeoLanding slug="ac-service-hyderabad" />} />
      <Route path="/home-appliance-repair-hyderabad" element={<ServiceBooking kind="appliance" />} />
      <Route path="/home-cleaning-hyderabad" element={<SeoLanding slug="home-cleaning-hyderabad" />} />
      <Route path="/sofa-cleaning-hyderabad" element={<SeoLanding slug="sofa-cleaning-hyderabad" />} />
      <Route path="/sofa-repair-hyderabad" element={<ServiceBooking kind="sofa" />} />
      <Route path="/our-services" element={<OurServices />} />
      <Route path="/locations/hyderabad" element={<LocationHyderabad />} />


      {/* Protected customer area */}
      <Route
        element={
          <Protected>
            <DashboardLayout />
          </Protected>
        }
      >

        <Route
          path="/dashboard"
          element={<Dashboard />}
        />

        <Route
          path="/bookings"
          element={<Bookings />}
        />

        <Route
          path="/vehicles"
          element={<Vehicles />}
        />
        <Route
          path="/referral"
          element={<Referral />}
        />

        <Route
          path="/services"
          element={<Services />}
        />

        <Route path="/addons" element={<Addons />} />

        <Route
          path="/subscription"
          element={<Subscription />}
        />

        <Route
          path="/profile"
          element={<Profile />}
        />

        {/* Admin */}
        <Route
          path="/admin"
          element={
            <Protected adminOnly panel="carwash">
              <Admin />
            </Protected>
          }
        />

        <Route
          path="/admin/extra-services"
          element={
            <Protected adminOnly panel="extra">
              <ExtraServicesAdmin />
            </Protected>
          }
        />

        <Route
          path="/admin/jobs"
          element={
            <Protected adminOnly panel="jobs">
              <JobsAdmin />
            </Protected>
          }
        />

      </Route>


      {/* Unknown URL */}
      <Route
        path="*"
        element={<Navigate to="/" replace />}
      />

    </Routes>
    </Suspense>
  );
}


/*
 * Main application
 */
if (typeof window !== "undefined" && "serviceWorker" in navigator) {
  window.addEventListener("load", async () => {
    try {
      const registration = await navigator.serviceWorker.register("/sw.js", { scope: "/" });
      if (registration.waiting) registration.waiting.postMessage({ type: "SKIP_WAITING" });
      // Look for a new deployment now, every 5 minutes and whenever the tab becomes visible again.
      const check = () => registration.update().catch(() => {});
      setInterval(check, 5 * 60 * 1000);
      document.addEventListener("visibilitychange", () => { if (!document.hidden) check(); });
    } catch {}
  });
  // When a NEW service worker takes over (= a new deployment), reload once so the page runs the new code.
  let reloaded = false;
  const hadController = !!navigator.serviceWorker.controller;
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (!hadController || reloaded) return;          // first install: nothing to refresh
    reloaded = true;
    window.location.reload();
  });
}

// Second safety net (works even without a service worker): compare the running bundle with the one the server serves now.
if (typeof window !== "undefined") {
  const runningBundle = () => (Array.from(document.scripts).map((s) => s.src).find((u) => /\/static\/js\/main\.[0-9a-f]+\.js/.test(u)) || "").match(/main\.[0-9a-f]+\.js/)?.[0];
  const checkVersion = async () => {
    try {
      const mine = runningBundle(); if (!mine) return;
      const html = await (await fetch("/index.html", { cache: "no-store" })).text();
      const live = html.match(/main\.[0-9a-f]+\.js/)?.[0];
      if (live && live !== mine && !sessionStorage.getItem("navi_ver_reload")) {
        sessionStorage.setItem("navi_ver_reload", live);
        if (window.caches) { const keys = await caches.keys(); await Promise.all(keys.filter((k) => k.startsWith("navi-care-v")).map((k) => caches.delete(k))); }
        window.location.reload();
      }
    } catch { /* offline: try again later */ }
  };
  window.addEventListener("load", () => setTimeout(checkVersion, 4000));
  document.addEventListener("visibilitychange", () => { if (!document.hidden) checkVersion(); });
  setInterval(checkVersion, 10 * 60 * 1000);
}

export default function App() {
  return (
    <ThemeProvider>

      <BrowserRouter>

        <AuthProvider>

          <AppRouter />
          <OfflineBanner />
          <PaymentRetryBar />
          <GlobalChatBot />

          <Toaster
            position="top-center"
            richColors
          />

        </AuthProvider>

      </BrowserRouter>

    </ThemeProvider>
  );
}
